<template>
  <transition name="fade">
    <div class="sessionLoader">
      <CofocusLogo :isSessionLoader="true" />
      <div class="loader-text">Loading with magic...</div>
      <div v-if="troubleJoining && !troubleshoot" class="trouble-joining" @click="troubleshoot = true">
        {{ `Trouble joining the session?` }}
      </div>
      <div v-if="rebootButton && !retryingText" @click="attemptReboot" class="troubleshoot--attempt-reboot">
        Attempt reconnect
      </div>
      <div v-if="retryingText" class="troubleshoot--reconnecting">
        {{ retryingText }}
      </div>
      <div class="troubleshoot" v-if="troubleshoot">
        <div class="troubleshoot-header">
          <div class="troubleshoot-title">Stuck at the loading screen?</div>
          <div class="troubleshoot-subtitle">Reasons why it might be happening</div>
          <ul class="troubleshoot-group">
            <li class="troubleshoot-options">You have not allowed camera or microphone access to Cofocus. Remember that camera is strictly mandatory on Cofocus.</li>
            <li class="troubleshoot-options">Another tab, window or application is already using your camera or microphone (such as Zoom, Loom, Google Meet or even another open Cofocus session tab).</li>
            <li class="troubleshoot-options">Your internet connection is unstable (this could also be caused by using a VPN).</li>
          </ul>
          <div class="troubleshoot-alternative">Alternatively, sometimes restarting the device fixes the problem.</div>
          <div class="troubleshoot-info">Send us an email at hello@cofocus.one if you tried all of the above and were still not able to access the session. We'll help troubleshoot this further.</div>
        </div>
        <CloseModalButton @closeModal="closeTroubleshoot" />
      </div>
    </div>
  </transition>
</template>

<script>
import CofocusLogo from "../../../../components/Nav/NavComponents/CofocusLogo.vue";
import CloseModalButton from "../../../../components/Widgets/CloseModalButton/CloseModalButton.vue";
import { registerStuckTimestamp } from "../SessionHelpers/timestamps";

import { attemptLaunchVideo } from "../SessionHelpers/Room/room";

export default {
  name: "LoadingSession",
  components: {
    CofocusLogo,
    CloseModalButton,
  },
  props: {
    loading: Boolean,
    leaving: Boolean,
  },
  data() {
    return {
      troubleJoining: false,
      troubleshoot: false,
      rebootButton: false,

      rebooting: false,
      retryingText: "",
      reconnectionAttempts: 0,
      maxAttempts: 5, // limit of attempts we'll try to reconnect user automatically
      reconnectionFailedState: false, // if reached max attempts, then set to fail

      firstEnterTime: 0,
      lastReconnectAttemptTime: 0,

      stuckChecking: null,
      reconnector: null,
    };
  },
  async created() {
    this.firstEnterTime = Date.now();
    this.stuckChecking = setInterval(this.checkStuckState, 1000);
  },
  methods: {
    async delay(ms) {
      return new Promise((res) => setTimeout(res, ms));
    },

    async attemptReboot() {
      this.retryingText = "Reconnecting...";
      await attemptLaunchVideo();
      await this.delay(2500);
      this.retryingText = "";
    },

    async autoRebooter() {
      const isFirstTry = this.reconnectionAttempts === 0;
      const nowMS = Date.now();
      if (this.reconnectionAttempts <= this.maxAttempts) {
        /* If it's the user's first reconnection attempt */
        const comparisonTime = isFirstTry ? this.firstEnterTime : this.lastReconnectAttemptTime;

        /* Depending on time passed since first try / last reconnect attempt, display retrying message */
        if (nowMS > comparisonTime + 10000) {
          this.retryingText = "Retrying in... 3";
        }
        if (nowMS > comparisonTime + 11000) {
          this.retryingText = "Retrying in... 2";
        }
        if (nowMS > comparisonTime + 12000) {
          this.retryingText = "Retrying in... 1";
        }
        if (nowMS > comparisonTime + 13000) {
          this.retryingText = "Attempting re-launch Cofocus video";
          await attemptLaunchVideo();
          this.retryingText = "";
          this.reconnectionAttempts++;
          this.lastReconnectAttemptTime = Date.now();
        }
      } else {
        this.reconnect = null;
        this.reconnectionFailedState = true;
        this.retryingText = "Reconnection attempts failed.";
      }
    },

    async checkStuckState() {
      /* If loading screen no longer showing, don't do anything */
      if (this.loading === false || this.leaving === true) return;

      /* This interval's time */
      const nowMS = Date.now();

      /* If user is stuck for 5 seconds since first enter, display troubleshooting option */
      if (nowMS > this.firstEnterTime + 4000 && this.troubleJoining === false) {
        this.troubleJoining = true;
      }

      /* If user is stuck for 10 seconds, offer them a reboot button */
      if (nowMS > this.firstEnterTime + 10000) {
        this.rebootButton = true;
        clearInterval(this.stuckChecking);
        this.stuckChecking = null;
        await registerStuckTimestamp();
      }

      /* If user is stuck for 10 seconds and reconnector not yet initialized,
       * initialize reconnector for automatic reboot attempts of the video experience */
      /* if (nowMS > this.firstEnterTime + 9000 && !this.reconnector && !this.reconnectionFailedState) {
        this.rebooting = true;
        this.stuckChecking = null;
        await registerStuckTimestamp();
        this.reconnector = setInterval(this.autoRebooter, 10000);
      } */
    },

    closeTroubleshoot() {
      this.troubleshoot = false;
    },
  },
};
</script>

<style scoped>
.trouble-joining {
  font-size: 17px;
  text-align: center;
  padding: 10px 12px;
  line-height: 26px;
  background-color: white;
  margin-bottom: -92px;
  margin-top: 46px;
  font-weight: 600;
  text-decoration: underline;
  text-underline-offset: 5px;
  -webkit-text-decoration-color: #424242;
  /* text-decoration-color: #424242; */
  letter-spacing: -0.1px;
  color: #606060;
  /* position: absolute;
  bottom: 50px;
  right: 15px; */
  cursor: pointer;
}
.trouble-joining:hover {
  color: #222;
}
.troubleshoot {
  width: 420px;
  border-radius: 12px;
  margin-top: -20px;
  font-size: 22px;
  margin-bottom: 50px;
  text-align: center;
  padding: 10px 12px;
  line-height: 26px;
  background-color: white;
  box-shadow: rgb(15 15 15 / 5%) 0px 0px 0px 1px, rgb(0 0 0 / 10%) 0px 3px 6px, rgb(245 245 245 / 20%) 0px 9px 24px;
  position: absolute;
  height: 400px;
}
.troubleshoot-header {
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0px auto;
  flex-direction: column;
  box-sizing: border-box;
  margin-top: 30px;
  margin-bottom: 15px;
}
.troubleshoot-title {
  font-size: 22px;
  text-align: center;
}
.troubleshoot-group {
  width: 325px;
  padding: 20px 0px;
  padding-bottom: 10px;
}
.troubleshoot-options {
  list-style: disc;
  font-size: 14px;
  line-height: 20px;
  margin-bottom: 10px;
  text-align: left;
}
.troubleshoot-subtitle {
  font-size: 16px;
  line-height: 22px;
  font-weight: 500;
  width: 325px;
  text-align: center;
  margin-top: 6px;
}
.troubleshoot-alternative {
  font-size: 14px;
}
.troubleshoot-info {
  font-size: 12px;
  line-height: 16px;
  margin-top: 10px;
  font-weight: 600;
}

.troubleshoot--attempt-reboot,
.troubleshoot--reconnecting {
  font-size: 17px;
  text-align: center;
  padding: 6px 14px;
  line-height: 26px;
  background-color: #222;
  margin-bottom: -156px;
  margin-top: 110px;
  font-weight: 600;
  color: white;
  cursor: pointer;
  border-radius: 360px;
}
.troubleshoot--attempt-reboot:hover {
  background-color: #333;
}

.troubleshoot--reconnecting {
  cursor: default;
}

.fade-leave-to
/* .fade-leave-active in <2.1.8 */ {
  opacity: opacity 0.3s ease;
}

.fade-leave-active {
  transition: opacity 0.5s ease;
}

.fade-leave-to
/* .fade-leave-active in <2.1.8 */ {
  opacity: 0;
}
@keyframes fade {
  0% {
    transform: scale(0);
  }
  50% {
    transform: scale(1.5);
  }
  100% {
    transform: scale(1);
  }
}
</style>
